import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { UsageByEndpointChart } from 'src/App/Dashboards/Api/Components';
import {
  fetchPublicApiLedgerAdmin,
  PublicApiLedgerPayload,
} from './helper';
import {
  COLOR_BACKGROUND_2,
  COLOR_GREY_200,
  COLOR_PRIMARY_1,
  COLOR_TEXT_2,
  COLOR_TEXT_3,
} from 'src/utils/colors';
import { formatUnixInAdminTz, shortTzLabel } from '../adminDisplayTime';

type ApiProps = {
  adminPassword: string | null;
  displayTimeZone: string;
};

const ADMIN_TABLE_HEADER_BG = '#F1F5F9';
const ADMIN_TABLE_SURFACE_BG = '#FFFFFF';

/**
 * Admin API tab — public API ledger: last 30 days stacked by route + recent calls table.
 */
const Api = ({ adminPassword, displayTimeZone }: ApiProps) => {
  const [ledger, setLedger] = useState<PublicApiLedgerPayload | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [failedEmailDraft, setFailedEmailDraft] = useState('');
  const [failedEmailApplied, setFailedEmailApplied] = useState('');

  const load = useCallback(async () => {
    if (!adminPassword) {
      setLedger(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    const result = await fetchPublicApiLedgerAdmin(adminPassword, {
      failedEmailSearch: failedEmailApplied.trim() || undefined,
    });
    setLoading(false);
    if (result.ok) {
      setLedger(result.data);
    } else {
      setLedger(null);
      setError(result.error);
    }
  }, [adminPassword, failedEmailApplied]);

  useEffect(() => {
    void load();
  }, [load]);

  const chartUsageData = useMemo(() => {
    if (!ledger || ledger.total_in_window === 0) {
      return {
        grouped_usage_data: {} as Record<string, Record<string, number>>,
        total_usage_count: 0,
        total_cost: 0,
      };
    }
    return {
      grouped_usage_data: ledger.grouped_usage_data,
      total_usage_count: ledger.total_in_window,
      total_cost: 0,
    };
  }, [ledger]);

  /** One row per UTC day in the window, chronological; sum of ledger `cost` for that day. */
  const dailyCostRows = useMemo(() => {
    const m = ledger?.daily_cost_totals;
    if (!m || typeof m !== 'object') return [];
    return Object.entries(m)
      .map(([date, total_cost]) => ({
        id: date,
        date,
        total_cost: typeof total_cost === 'number' ? total_cost : 0,
      }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [ledger]);

  const dailyCostColumns = useMemo(
    (): GridColDef[] => [
      { field: 'date', headerName: 'Day (UTC)', width: 140 },
      {
        field: 'total_cost',
        headerName: 'Total cost (sum)',
        width: 160,
        type: 'number',
        valueFormatter: params => {
          const v = params.value as number;
          if (v == null || Number.isNaN(v)) return '';
          return Number(v).toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 6,
          });
        },
      },
    ],
    []
  );

  /** Sum calls per route across all days in the window; highest usage first. */
  const routeTotalsRows = useMemo(() => {
    if (!ledger?.grouped_usage_data) return [];
    const totals: Record<string, number> = {};
    Object.values(ledger.grouped_usage_data).forEach(dayData => {
      Object.entries(dayData).forEach(([route, count]) => {
        const n = typeof count === 'number' ? count : 0;
        totals[route] = (totals[route] || 0) + n;
      });
    });
    return Object.entries(totals)
      .map(([route, total_calls]) => ({
        id: route,
        route,
        total_calls,
      }))
      .sort((a, b) => b.total_calls - a.total_calls);
  }, [ledger]);

  const routeTotalsColumns = useMemo(
    (): GridColDef[] => [
      { field: 'route', headerName: 'API route', flex: 1, minWidth: 280 },
      {
        field: 'total_calls',
        headerName: 'Total calls',
        width: 130,
        type: 'number',
      },
    ],
    []
  );

  const tableRows = useMemo(() => {
    if (!ledger?.recent_calls?.length) return [];
    return ledger.recent_calls.map((r, i) => ({
      id: `${r.timestamp}-${i}`,
      ...r,
    }));
  }, [ledger]);

  const userSpendingRows = useMemo(() => {
    const list = ledger?.user_spending;
    if (!Array.isArray(list) || !list.length) return [];
    return list.map((row, i) => ({
      id: `${row.user_email}-${i}`,
      user_email: row.user_email,
      total_spent: row.total_spent,
    }));
  }, [ledger]);

  const userSpendingColumns = useMemo(
    (): GridColDef[] => [
      { field: 'user_email', headerName: 'User email', flex: 1, minWidth: 260 },
      {
        field: 'total_spent',
        headerName: 'Total spent (30d)',
        width: 180,
        type: 'number',
        valueFormatter: params => {
          const v = params.value as number;
          if (v == null || Number.isNaN(v)) return '';
          return Number(v).toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 6,
          });
        },
      },
    ],
    []
  );

  const failedCallsRows = useMemo(() => {
    const list = ledger?.failed_recent_calls;
    if (!Array.isArray(list) || !list.length) return [];
    return list.map((r, i) => ({
      id: `f-${r.timestamp}-${i}`,
      ...r,
    }));
  }, [ledger?.failed_recent_calls]);

  const failedCallsColumns = useMemo((): GridColDef[] => {
    const tzShort = shortTzLabel(displayTimeZone);
    return [
      {
        field: 'timestamp',
        headerName: `Time (${tzShort})`,
        width: 220,
        valueFormatter: params => {
          const ts = params.value as number;
          if (!ts) return '';
          return formatUnixInAdminTz(ts, displayTimeZone);
        },
      },
      { field: 'route_key', headerName: 'Route', flex: 1, minWidth: 200 },
      { field: 'deny_reason', headerName: 'Deny reason', flex: 1, minWidth: 160 },
      { field: 'user_email', headerName: 'User email', flex: 1, minWidth: 220 },
      {
        field: 'cost',
        headerName: 'Cost',
        width: 80,
        valueFormatter: params => (params.value != null ? String(params.value) : ''),
      },
    ];
  }, [displayTimeZone]);

  const recentCallsColumns = useMemo((): GridColDef[] => {
    const tzShort = shortTzLabel(displayTimeZone);
    return [
      {
        field: 'timestamp',
        headerName: `Time (${tzShort})`,
        width: 220,
        valueFormatter: params => {
          const ts = params.value as number;
          if (!ts) return '';
          return formatUnixInAdminTz(ts, displayTimeZone);
        },
      },
      { field: 'route_key', headerName: 'Route', flex: 1, minWidth: 220 },
      {
        field: 'cost',
        headerName: 'Cost',
        width: 90,
        valueFormatter: params => (params.value != null ? String(params.value) : ''),
      },
      {
        field: 'user_email',
        headerName: 'User email',
        flex: 1,
        minWidth: 240,
      },
    ];
  }, [displayTimeZone]);

  return (
    <Box sx={{ width: '100%', py: 1, overflowY: 'auto' }}>
      <Paper
        elevation={0}
        sx={{
          p: 3,
          maxWidth: 1100,
          mx: 'auto',
          border: `1px solid ${COLOR_GREY_200}`,
          borderRadius: 2,
          bgcolor: COLOR_BACKGROUND_2,
          boxShadow: '0 1px 3px rgba(15, 23, 42, 0.06)',
        }}
      >
        <Typography variant="h5" sx={{ color: COLOR_TEXT_2, fontWeight: 600, mb: 0.5 }}>
          API
        </Typography>
        <Typography variant="body2" sx={{ color: COLOR_TEXT_3, mb: 2 }}>
          Public API usage from DynamoDB <code>public_api_usage_ledger</code> — last 30 days, up to 1,000 most
          recent calls in that window. Table times use the display timezone above (MYT default); chart day
          buckets stay UTC on the server.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!adminPassword && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Enter the admin password to load usage data.
          </Alert>
        )}

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: COLOR_PRIMARY_1 }} />
          </Box>
        )}

        {!loading && adminPassword && ledger && (
          <>
            <Typography variant="body2" sx={{ color: COLOR_TEXT_3, mb: 1 }}>
              Total calls in window: <strong>{ledger.total_in_window}</strong>
              {ledger.total_cost_in_window != null && (
                <>
                  {' '}
                  · Total cost in window:{' '}
                  <strong>
                    {Number(ledger.total_cost_in_window).toLocaleString(undefined, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 6,
                    })}
                  </strong>
                </>
              )}
            </Typography>
            <Box sx={{ mb: 2 }}>
              <UsageByEndpointChart
                data={chartUsageData}
                title="Calls by route (last 30 days — daily totals by UTC date)"
                hideLegend
              />
            </Box>

            {dailyCostRows.length > 0 && (
              <>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: COLOR_TEXT_2, mb: 0.5 }}>
                  Cost by day (UTC)
                </Typography>
                <Typography variant="body2" sx={{ color: COLOR_TEXT_3, mb: 1.5 }}>
                  Sum of per-call <code>cost</code> from the ledger for each calendar day (same 30-day window as
                  the chart).
                </Typography>
                <Box
                  sx={{
                    width: '100%',
                    height: 400,
                    border: `1px solid ${COLOR_GREY_200}`,
                    borderRadius: 2,
                    overflow: 'hidden',
                    bgcolor: ADMIN_TABLE_SURFACE_BG,
                    mb: 3,
                  }}
                >
                  <DataGrid
                    rows={dailyCostRows}
                    columns={dailyCostColumns}
                    pageSize={25}
                    rowsPerPageOptions={[25, 50, 100]}
                    disableSelectionOnClick
                    density="compact"
                    hideFooter={dailyCostRows.length <= 25}
                    sx={{
                      border: 'none',
                      '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: ADMIN_TABLE_HEADER_BG,
                        fontWeight: 600,
                      },
                      '& .MuiDataGrid-main': { backgroundColor: ADMIN_TABLE_SURFACE_BG },
                    }}
                  />
                </Box>
              </>
            )}

            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: COLOR_TEXT_2, mb: 0.5 }}>
              Total calls per route (full window)
            </Typography>
            <Typography variant="body2" sx={{ color: COLOR_TEXT_3, mb: 1.5 }}>
              Sorted by usage (highest first). Same period as the chart above.
            </Typography>
            <Box
              sx={{
                width: '100%',
                height: 400,
                border: `1px solid ${COLOR_GREY_200}`,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: ADMIN_TABLE_SURFACE_BG,
                mb: 3,
              }}
            >
              <DataGrid
                rows={routeTotalsRows}
                columns={routeTotalsColumns}
                pageSize={25}
                rowsPerPageOptions={[25, 50, 100]}
                disableSelectionOnClick
                density="compact"
                hideFooter={routeTotalsRows.length <= 25}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: ADMIN_TABLE_HEADER_BG,
                    fontWeight: 600,
                  },
                  '& .MuiDataGrid-main': { backgroundColor: ADMIN_TABLE_SURFACE_BG },
                }}
              />
            </Box>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: COLOR_TEXT_2, mb: 0.5 }}>
              Spending by user (30-day window)
            </Typography>
            <Typography variant="body2" sx={{ color: COLOR_TEXT_3, mb: 1.5 }}>
              Sum of per-call <code>cost</code> per resolved user email (same window as chart). Only users
              with total spending &gt; 0 are listed.
            </Typography>
            <Box
              sx={{
                width: '100%',
                height: 400,
                border: `1px solid ${COLOR_GREY_200}`,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: ADMIN_TABLE_SURFACE_BG,
                mb: 3,
              }}
            >
              <DataGrid
                rows={userSpendingRows}
                columns={userSpendingColumns}
                pageSize={25}
                rowsPerPageOptions={[25, 50, 100]}
                disableSelectionOnClick
                density="compact"
                hideFooter={userSpendingRows.length <= 25}
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: ADMIN_TABLE_HEADER_BG,
                    fontWeight: 600,
                  },
                  '& .MuiDataGrid-main': { backgroundColor: ADMIN_TABLE_SURFACE_BG },
                }}
              />
            </Box>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: COLOR_TEXT_2, mb: 1 }}>
              Most recent API calls
            </Typography>
            <Typography variant="body2" sx={{ color: COLOR_TEXT_3, mb: 1.5 }}>
              Newest first (max {ledger.recent_limit ?? 1000} rows).
            </Typography>
            <Box
              sx={{
                width: '100%',
                height: 480,
                border: `1px solid ${COLOR_GREY_200}`,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: ADMIN_TABLE_SURFACE_BG,
              }}
            >
              <DataGrid
                rows={tableRows}
                columns={recentCallsColumns}
                pageSize={25}
                rowsPerPageOptions={[25, 50, 100]}
                disableSelectionOnClick
                density="compact"
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: ADMIN_TABLE_HEADER_BG,
                    fontWeight: 600,
                  },
                  '& .MuiDataGrid-main': { backgroundColor: ADMIN_TABLE_SURFACE_BG },
                }}
              />
            </Box>

            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: COLOR_TEXT_2, mt: 3, mb: 0.5 }}>
              Failed API calls (30-day window)
            </Typography>
            <Typography variant="body2" sx={{ color: COLOR_TEXT_3, mb: 1 }}>
              Denied attempts only (max {ledger.failed_recent_limit ?? 1000} rows after server filter).
              Search filters by resolved user email substring; unmapped keys show as (unknown).
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5, alignItems: 'center' }}>
              <TextField
                size="small"
                label="Filter by email contains"
                value={failedEmailDraft}
                onChange={e => setFailedEmailDraft(e.target.value)}
                sx={{ minWidth: 280 }}
              />
              <Button
                variant="contained"
                size="medium"
                onClick={() => setFailedEmailApplied(failedEmailDraft.trim())}
                sx={{ textTransform: 'none', fontWeight: 600 }}
              >
                Search
              </Button>
            </Box>
            <Box
              sx={{
                width: '100%',
                height: 480,
                border: `1px solid ${COLOR_GREY_200}`,
                borderRadius: 2,
                overflow: 'hidden',
                bgcolor: ADMIN_TABLE_SURFACE_BG,
              }}
            >
              <DataGrid
                rows={failedCallsRows}
                columns={failedCallsColumns}
                pageSize={25}
                rowsPerPageOptions={[25, 50, 100]}
                disableSelectionOnClick
                density="compact"
                sx={{
                  border: 'none',
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: ADMIN_TABLE_HEADER_BG,
                    fontWeight: 600,
                  },
                  '& .MuiDataGrid-main': { backgroundColor: ADMIN_TABLE_SURFACE_BG },
                }}
              />
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
};

export default Api;
